import type { FC } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Avatar, Box } from '@chakra-ui/react';
import { BICtoBankInfo } from '@shinetools/iban-bban-bic';
import { printFormat } from 'iban';

import { formatAmount } from 'common/formatAmount';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import BackButton from 'components/BackButton';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import TransferInformationRow from 'components/TransferInformationRow';
import { UserBankDetailsFormSchema } from 'components/UserBankDetailsForm/schema';
import * as Layout from 'layouts/StickyStepperLayout';

import { useSubmitExpenseClaimRequest } from '../../../../../../hooks/useSubmitExpenseClaimRequest';
import * as routes from '../../../../../../routes';
import { useNewExpenseClaimRequestState } from '../../../NewExpenseClaimRequestProvider';
import { ReceiptInformationFormSchema } from '../ReceiptInformation/components/ReceiptInformationForm/schema';
import locales from './locales';

export const TransferOverview: FC<StepProps> = ({ goNext }) => {
  const { showToast } = useToast();
  const history = useHistory();
  const [submitExpenseClaimRequest, { loading }] = useSubmitExpenseClaimRequest(
    {
      onCompleted: () => {
        showToast({
          message: locales.submitSuccess,
          type: 'success',
        });
        history.push(routes.root);
      },
    },
  );

  const {
    receiptInformation,
    recipientInformation,
    shouldSkipRecipientInformation,
  } = useNewExpenseClaimRequestState();

  const handleConfirm = () => {
    const input = {
      ...receiptInformation,
      ...recipientInformation,
    } as ReceiptInformationFormSchema & UserBankDetailsFormSchema;

    return submitExpenseClaimRequest(input);
  };

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton
            onClick={() => {
              goNext({
                key: shouldSkipRecipientInformation
                  ? 'receipt-information'
                  : 'recipient-information',
                shouldReplaceCurrentStep: true,
              });
            }}
          />
        </Layout.HeaderButtonsContainer>
        <Layout.ContentsContainer gap="space-40">
          <Heading size="lg">{locales.title}</Heading>
          <SunshineCard.Group>
            {receiptInformation.amount?.total ? (
              <TransferInformationRow
                label={locales.labels.amount}
                value={locales.formatString(locales.values.amount, {
                  total: formatAmount({
                    forcePlusSign: false,
                    value: receiptInformation.amount.total,
                    withNoMinorUnitDigits: true,
                  }),
                })}
              />
            ) : null}
            <Box
              display="contents"
              sx={{
                '& hr': {
                  margin: '0 auto',
                  maxWidth: '95%',
                },
              }}
            >
              <SunshineCard.Group border="none" isBoxed>
                {(receiptInformation.amount?.vatItems ?? []).map(
                  (vatItem, index) => {
                    return (
                      <TransferInformationRow
                        isBold={false}
                        key={index}
                        label={locales.formatString(locales.labels.vatRate, {
                          rate: vatItem?.rate ?? 0,
                        })}
                        value={formatAmount({
                          forcePlusSign: false,
                          value: vatItem?.amount ?? 0,
                          withNoMinorUnitDigits: true,
                        })}
                      />
                    );
                  },
                )}
              </SunshineCard.Group>
            </Box>

            {receiptInformation.reseller ? (
              <TransferInformationRow
                isBold={false}
                label={locales.labels.reseller}
                value={receiptInformation.reseller}
              />
            ) : null}
            {receiptInformation.description ? (
              <SunshineCard alignItems="flex-start">
                <SunshineCard.Content paddingTop="space-0" spacing="space-8">
                  <SunshineCard.Text size="md">
                    {locales.labels.description}
                  </SunshineCard.Text>
                  <SunshineCard.Text size="sm" variant="light">
                    {receiptInformation.description}
                  </SunshineCard.Text>
                </SunshineCard.Content>
              </SunshineCard>
            ) : null}
            {receiptInformation.supportingDocument?.fileRef ? (
              <SunshineCard
                alignItems="flex-start"
                onClick={() => {
                  goNext({
                    key: 'receipt-information',
                    shouldReplaceCurrentStep: true,
                  });
                }}
              >
                <SunshineCard.Slot name="action">
                  <SunshineCard.SunshineIcon name="chevron-right" />
                </SunshineCard.Slot>
                <SunshineCard.Content paddingTop="space-0" spacing="space-8">
                  <SunshineCard.Text size="md">
                    {locales.labels.supportingDocument}
                  </SunshineCard.Text>
                </SunshineCard.Content>
              </SunshineCard>
            ) : null}
          </SunshineCard.Group>
          {recipientInformation.name &&
          recipientInformation.iban &&
          recipientInformation.swiftBic ? (
            <SunshineCard
              alignItems="center"
              onClick={() => {
                goNext({
                  key: 'recipient-information',
                  shouldReplaceCurrentStep: true,
                });
              }}
              title={recipientInformation.name}
            >
              <SunshineCard.Slot name="aside">
                <Avatar
                  background="grey.100"
                  borderColor="border"
                  borderRadius="radius-10"
                  borderWidth="thin"
                  boxSize="space-32"
                  color="text-primary"
                  fontWeight="weight-500"
                  name={recipientInformation.name}
                />
              </SunshineCard.Slot>
              <SunshineCard.Content>
                <SunshineCard.Text>
                  {locales.formatString(locales.editIbanValue, {
                    bankName: BICtoBankInfo(recipientInformation.swiftBic).name,
                    iban: printFormat(recipientInformation.iban),
                  })}
                </SunshineCard.Text>
              </SunshineCard.Content>
              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon alignSelf="flex-start" name="edit" />
              </SunshineCard.Slot>
            </SunshineCard>
          ) : null}
        </Layout.ContentsContainer>
        <Layout.FooterContainer>
          <StepperProgressBar />

          <Layout.FooterButtonsContainer
            alignItems="center"
            justifyContent="space-between"
          >
            <Button as={Link} to={routes.root} variant="inline-secondary">
              {locales.cancel}
            </Button>
            <Button
              icon="send"
              iconPosition="right"
              isLoading={loading}
              onClick={handleConfirm}
              type="submit"
            >
              {locales.submit}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};
